import { useEffect } from "react";

/**
 * Reusable SEO component that dynamically updates document.title,
 * meta tags, canonical link, Open Graph, Twitter cards, and JSON-LD schema in <head>.
 */
export default function SEO({
  title,
  description,
  keywords,
  canonical,
  ogImage = "https://www.entelipro.com/logo512.png",
  ogType = "website",
  schema = null,
}) {
  useEffect(() => {
    // 1. Update Document Title
    const siteTitle = "EnteliPro";
    const fullTitle = title ? `${title} | ${siteTitle}` : "EnteliPro AI | AI ERP & CRM Software";
    document.title = fullTitle;

    // Helper function to update or create a <meta> tag in <head>
    const setMetaTag = (attrName, attrValue, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[${attrName}="${attrValue}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    // Helper function to update or create a <link> tag in <head>
    const setLinkTag = (rel, href) => {
      if (!href) return;
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    // 2. Primary Meta Tags
    setMetaTag("name", "description", description);
    if (keywords) {
      setMetaTag("name", "keywords", keywords);
    }

    // ✅ 3. Canonical Link – Dynamic based on environment
    const isLocalhost =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    const cleanCanonical = isLocalhost
      ? window.location.href.split("?")[0] // ✅ Localhost URL
      : (canonical || window.location.href.split("?")[0]); // ✅ Production URL

    setLinkTag("canonical", cleanCanonical);

    // 4. Open Graph Tags
    setMetaTag("property", "og:title", fullTitle);
    setMetaTag("property", "og:description", description);
    setMetaTag("property", "og:url", cleanCanonical);
    setMetaTag("property", "og:type", ogType);
    setMetaTag("property", "og:image", ogImage);
    setMetaTag("property", "og:site_name", siteTitle);

    // 5. Twitter Card Tags
    setMetaTag("name", "twitter:card", "summary_large_image");
    setMetaTag("name", "twitter:title", fullTitle);
    setMetaTag("name", "twitter:description", description);
    setMetaTag("name", "twitter:image", ogImage);

    // 6. JSON-LD Structured Data Schema
    let scriptTag = document.getElementById("structured-data-jsonld");
    if (schema) {
      if (!scriptTag) {
        scriptTag = document.createElement("script");
        scriptTag.id = "structured-data-jsonld";
        scriptTag.type = "application/ld+json";
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(schema);
    } else if (scriptTag) {
      scriptTag.remove();
    }
  }, [title, description, keywords, canonical, ogImage, ogType, schema]);

  return null; // This component operates strictly in document.head
}